<template>
  <Toast />
  <ConfirmDialog group="headless">
    <template #container="{ message, acceptCallback, rejectCallback }">
      <div class="flex flex-col items-center p-8 bg-surface-0 dark:bg-surface-900 rounded">
        <div
          class="rounded-full bg-white outline outline-black inline-flex justify-center items-center h-24 w-24 -mt-20">
          <i class="pi pi-question text-5xl"></i>
        </div>
        <span class="font-bold text-2xl block mb-2 mt-6">{{ message.header }}</span>
        <p class="mb-0">{{ message.message }}</p>
        <div class="flex items-center gap-2 mt-6">
          <Button :label="message.rejectLabel" outlined @click="rejectCallback" class="w-32"></Button>
          <Button :label="message.acceptLabel" @click="acceptCallback" class="w-32"></Button>
        </div>
      </div>
    </template>
  </ConfirmDialog>
  <IndexView />
  <ViewContainer>
    <section class="p-6">
      <PageTitle title="Data Pelanggan" />
      <div
        class="border-t-2 border-primary bg-white dark:bg-dark-900 w-full rounded-lg py-6 px-5 shadow-lg shadow-light-100 dark:shadow-dark-500">
        <div id="table-header" class="mb-5 flex justify-between gap-4">
          <AddButton to_url="/pelanggan/tambah-pelanggan" button_text="+ Tambah Pelanggan"
            button_class="text-base py-2 font-medium" />
          <InputTextCustom v-model="searchQuery" placeholder="Cari..." input_class="w-80 hidden" />
        </div>
        <TableComponent>
          <TableHead>
            <TableRow>
              <TableHeadCell>No</TableHeadCell>
              <TableHeadCell>Nama Pelanggan</TableHeadCell>
              <TableHeadCell>Email</TableHeadCell>
              <TableHeadCell>No Telepon</TableHeadCell>
              <TableHeadCell>Kota</TableHeadCell>
              <TableHeadCell>Action</TableHeadCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow v-if="allCustomers.length == 0">
              <TableCell colspan="6" customClass="text-center italic text-gray-400 dark:text-gray-500" noTextColor>
                Belum ada data
              </TableCell>
            </TableRow>
            <TableRow v-else v-for="(customer, index) in allCustomers" :key="customer.id" :index="index" isBody>
              <TableCell>{{ index + 1 }}</TableCell>
              <TableCell>{{ customer.fullname }}</TableCell>
              <TableCell>{{ customer.email }}</TableCell>
              <TableCell>{{ customer.phone }}</TableCell>
              <TableCell>{{ customer.city }}</TableCell>
              <TableCell class="text-gray-500 flex">
                <button @click="editCustomer(customer)" :id="'edit-data-' + customer.id"
                  class="bg-warning hover:bg-hover-warning flex justify-center items-center mx-1 p-2 rounded-md group">
                  <PencilSquareIcon class="h-4 w-4 text-light-900 group-hover:text-primary" />
                </button>
                <button :id="'delete-data-' + customer.id" @click="deleteCustomer(customer)"
                  class="bg-danger hover:bg-hover-danger flex justify-center items-center mx-1 p-2 rounded-md group">
                  <TrashIcon class="h-4 w-4 text-white group-hover:text-light-50" />
                </button>
              </TableCell>
            </TableRow>
          </TableBody>
        </TableComponent>
        <PaginationButtons v-model="currentPage" :totalPages="totalPages" />
      </div>
    </section>
  </ViewContainer>
</template>

<script>
import IndexView from '@/views/IndexView.vue';
import PageTitle from '@/components/PageTitle.vue';
import AddButton from '@/components/buttons/AddButton.vue';
import { mapGetters, mapActions } from 'vuex';
import { PencilSquareIcon, TrashIcon } from '@heroicons/vue/24/solid';
import { TableComponent, TableHead, TableBody, TableRow, TableCell, TableHeadCell } from '@/components/table';
import { Toast, ConfirmDialog, Button } from 'primevue';
import InputTextCustom from '@/components/form/InputTextCustom.vue';
import PaginationButtons from '@/components/PaginationButtons.vue';
import ViewContainer from '@/components/ViewContainer.vue';

export default {
  name: "CustomerView",
  components: {
    IndexView, PageTitle, InputTextCustom, AddButton, ViewContainer,
    PencilSquareIcon, TrashIcon, TableComponent, TableHead, TableBody, TableRow, TableCell, TableHeadCell, ConfirmDialog, Toast, Button, PaginationButtons
  },
  data() {
    return {
      searchQuery: '',
      currentPage: 1,
      totalPages: 1,
    }
  },
  computed: {
    ...mapGetters('customers', ['allCustomers'])
  },
  created() {
    this.$store.dispatch("customers/allCustomers", { limit: 50 });
  },
  watch: {
    currentPage: {
      immediate: true,
      handler(newVal) {
        this.fetchCustomers({ page: newVal });
      },
    }
  },
  methods: {
    ...mapActions('customers', ['deleteCustomerById']),
    async fetchCustomers(params = { page: 1, limit: 50 }) {
      if (!params.page) params.page = this.currentPage;
      if (!params.limit) params.limit = 50;

      try {
        const { pagination } = await this.$store.dispatch('customers/fetchCustomers', params);
        if (pagination) {
          this.totalPages = pagination.totalPages;
        }
      } catch (error) {
        console.log(error);
      }
    },
    editCustomer(customer) {
      this.$router.push({ name: 'EditCustomer', params: { id: customer.id } });
    },
    async deleteCustomer(customer) {
      this.$confirm.require({
        group: 'headless',
        header: 'Apakah anda yakin ingin menghapus customer ini?',
        message: 'Silahkan konfirmasi sebelum diproses.',
        acceptLabel: 'Ya',
        rejectLabel: 'Tidak',
        accept: async () => {
          try {
            await this.deleteCustomerById(customer.id);
            this.$store.dispatch('customers/fetchCustomers');
            this.$toast.add({ severity: 'success', summary: 'Success', detail: 'Berhasil menghapus customer.', life: 3000 });
          }
          catch (error) {
            console.error('Gagal menghapus customer.', error);
            this.$toast.add({ severity: 'error', summary: 'Failed', detail: 'Gagal menghapus customer.', life: 3000 });
          }
        },
        reject: () => {
          this.$toast.add({ severity: 'error', summary: 'Rejected', detail: 'Batal menghapus customer.', life: 3000 });
        }
      });
    }
  }
}
</script>